<template lang="pug">
    section
        el-row(:gutter="40")
            el-col(:lg="12" :md="12")
                el-form-item(:label="$t('Name')" prop="name")
                    el-input(type="text" v-model="name" maxlength="255" :show-word-limit="true")
                el-form-item(:label="$t('Description')" prop="description")
                    quill-editor(v-model="description" :options="quillConfig")
                el-form-item(:label="$t('Street')" prop="address.street")
                    el-input(v-model="street" maxlength="255" :show-word-limit="true")
                el-row.no-padding(:gutter="20")
                    el-col(:lg="8" :md="8")
                        el-form-item(:label="$t('Zipcode')" prop="address.zipcode")
                            el-input(v-model="zipcode" maxlength="255" :show-word-limit="true")
                    el-col(:lg="16" :md="16")
                        el-form-item(:label="$t('City')" prop="address.city")
                            el-input(v-model="city" maxlength="255" :show-word-limit="true")
                el-form-item(:label="$t('Country')" prop="address.country")
                    el-select.is-full-width(placeholder="" filterable v-model="country" autocomplete="no-fill")
                        el-option(v-for="(label, value) in countryOptions" :key="value" :label="label" :value="value")
                el-form-item(:label="$t('Image')")
                    .event-image-upload(v-loading="bannerUploading")
                        .event-image-preview(v-if="form.banner && form.banner.url")
                            a.image-link(href="#" @click.prevent="openImageEditor")
                                .banner-frame
                                    img(:src="croppedBannerSrc")
                                .edit-popover
                                    i.el-icon-fa-pencil-alt.fas
                            el-button.remove-image(
                                circle type="danger" size="mini"
                                icon="el-icon-fa-trash-alt far"
                                @click.stop="removeImage"
                            )
                        template(v-else)
                            el-upload.event-upload-box(
                                v-if="uploadEndpoint"
                                drag
                                :action="uploadEndpoint"
                                :headers="uploadHeaders"
                                :data="uploadData"
                                accept="image/jpg,image/jpeg,image/png,image/webp"
                                :show-file-list="false"
                                :on-success="onImageUpload"
                            )
                                i.el-icon-fa-image.fas
                                .el-upload__text {{ $t('Drop image or click') }}
                            el-button.select-library-btn(
                                plain size="small"
                                icon="el-icon-picture-outline"
                                @click="showLibraryDialog = true"
                            ) {{ $t('Select from library') }}

                    image-library-dialog(
                        :visible.sync="showLibraryDialog"
                        :multiple="false"
                        selection-type="image"
                        @select="onLibrarySelected"
                    )

                    banner-editor(
                        v-if="form.banner && form.banner.url"
                        :visible.sync="imageEditorVisible"
                        :image="form.banner"
                        @crop="onBannerCrop"
                        @reset="onBannerReset"
                    )

            el-col(:lg="12" :md="12")
                .flex-row
                    el-form-item.grow(:label="$t('Category')" prop="category")
                        el-select.is-full-width(
                            placeholder=""
                            filterable
                            :no-match-text="$t('No matching category')"
                            v-model="category"
                            valueKey="id"
                        )
                            el-option(
                                v-for="category in categoryOptions"
                                :key="category.id" :label="category.name" :value="category"
                            )
                                .category-option
                                    span.color(:style="`background: ${category.color}`")
                                    span {{ category.name }}
                    el-form-item.colorpicker(:label="$t('Color')" prop="color")
                        el-color-picker(v-model="color")

                el-form-item
                    el-switch(
                        :active-text="$t('Allow bookings')"
                        inactive-color="#ff4949"
                        v-model="booking"
                    )
                el-form-item.qr-option(v-if="booking")
                    .qr-option-inner
                        .qr-option-text
                            span.qr-option-label
                                i.el-icon-fa-qrcode.fas
                                |  {{ $t('Use QR code') }}
                            p.qr-option-desc {{ $t('Registered attendees receive a personal QR code by email that is scanned at the door.') }}
                        el-switch(
                            inactive-color="#ff4949"
                            v-model="useQrCode"
                        )
                transition(name="slide-fade")
                    el-form-item(v-if="booking" :label="$t('Booking confirmation text')")
                        quill-editor(v-model="bookingNotificationText" :options="quillConfigNoLinks")
                el-form-item.grow(v-if="booking" :label="$t('Max. number of bookings per slot')")
                    el-input(v-model="member_limit_per_slot" type="number" min="1")
                el-form-item.registration-template(v-if="booking && hasRegistrationTemplates" :label="$t('Registration form')")
                    el-select.is-full-width(
                        v-model="registrationTemplateId"
                        clearable
                        :placeholder="$t('Fixed fields only')"
                        :loading="registrationTemplateOptionsLoading"
                    )
                        el-option(
                            v-for="template in registrationTemplateOptions"
                            :key="template.id"
                            :label="template.name"
                            :value="template.id"
                        )
                            span {{ template.name }}
                            span.option-hint {{ $tc('{count} additional field(s)', template.fields_count || 0, { count: template.fields_count || 0 }) }}
                    .help {{ $t('First name, last name, email and slot are part of every form.') }}
                        |&nbsp;
                        router-link(:to="{ name: 'event-registration-template-list' }") {{ $t('Manage registration forms') }}
</template>

<i18n>
{
  "en": {
    "{count} additional field(s)": "no additional field | 1 additional field | {count} additional fields"
  },
  "de": {
    "Name": "Titel",
    "Description": "Beschreibung",
    "Color": "Farbe",
    "Image": "Event-Banner",
    "Drop image or click": "Bild hier ablegen oder klicken",
    "Select from library": "Aus Mediathek wählen",
    "Upload failed": "Upload fehlgeschlagen",
    "Allow bookings": "Anmeldungen zulassen",
    "Use QR code": "QR-Code verwenden",
    "Registered attendees receive a personal QR code by email that is scanned at the door.": "Angemeldete Personen erhalten einen persönlichen QR-Code per E-Mail, der beim Einlass gescannt werden kann.",
    "Booking confirmation text": "Bestätigungstext für Anmeldungen",
    "Max. number of bookings per slot": "Maximale Anzahl von Anmeldungen pro Slot",
    "Registration form": "Anmeldeformular",
    "Fixed fields only": "Nur Pflichtfelder",
    "First name, last name, email and slot are part of every form.": "Vorname, Nachname, E-Mail und Termin sind in jedem Formular enthalten.",
    "Manage registration forms": "Anmeldeformulare verwalten",
    "{count} additional field(s)": "kein Zusatzfeld | 1 Zusatzfeld | {count} Zusatzfelder",
    "Street": "Straße",
    "Zipcode": "PLZ",
    "City": "Ort",
    "Country": "Land",
    "Category": "Kategorie",
    "No matching category": "Keine passende Kategorie"
  }
}
</i18n>

<script>

import _ from 'lodash'
import axios from 'axios'
import { ensureFreshToken, getFitStringFromProcessObject } from '@/helpers'
import { useAuthStore, useSetupStore, useEventManagerEventStore } from '@/stores'

import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(en)
countries.registerLocale(de)

import NestedSet from '@/nested-set-lib'
import quillConfig from '@/quill/config'

import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'
import BannerEditor from './Partials/BannerEditor'

export default {
    name: 'Main',
    components: {
        ImageLibraryDialog,
        BannerEditor,
    },
    data() {
        quillConfig.modules.link.pages = this.getPageOptions

        const quillConfigNoLinks = _.cloneDeep(quillConfig)
        quillConfigNoLinks.modules.link.types = ['external', 'email', 'phone']

        return {
            quillConfig,
            quillConfigNoLinks,
            showLibraryDialog: false,
            imageEditorVisible: false,
            bannerUploading: false,
        }
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
            }
        },
        eventStore() {
            return useEventManagerEventStore();
        },
        setupStore() {
            return useSetupStore();
        },
        uploadEndpoint() {
            const cmsId = this.setupStore.activeCms?.id
            if (!cmsId) return ''
            return `${this.setupStore.uploadEndpoint}/public/${cmsId}/events`
        },
        uploadData() {
            return { 'context[type]': 'event' }
        },
        // Vorschau-URL: Original-File + optionaler Fokuspunkt-Zuschnitt (settings.processing).
        // Ohne Processing sorgt der 16:9-Rahmen (object-fit: cover) für den Standard-Zuschnitt.
        // Mit Processing wird bewusst die FileController-Route (/storage/public/{table}/{id}/{name})
        // gebaut – nur diese wendet die fit/crop-Parameter an (banner.url kann eine direkte
        // Storage-URL sein, z. B. DigitalOcean Spaces, die Query-Parameter ignoriert). Muster wie
        // in der Visitenkarten-Vorschau.
        croppedBannerSrc() {
            const banner = _.get(this.form, 'banner')
            const url = _.get(banner, 'url')
            if (!url) return ''
            const processing = _.get(banner, 'settings.processing')
            if (!processing) return url
            const query = getFitStringFromProcessObject(processing)
            if (!query) return url
            const route = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const table = banner.upload ? 'uploads' : 'images'
            return `${route}/${table}/${banner.id}/${banner.name}?${query}`
        },
        color: {
            get() {
                // return _.get(this.form, 'color', '')
                return _.get(this.form, 'color') || _.get(this.form, 'category.color', '')
            },
            set(value) {
                this.eventStore.setFormColor(value);
            }
        },
        name: {
            get() {
                return _.get(this.form, 'name', '')
            },
            set(value) {
                this.eventStore.setFormName(value);
            }
        },
        description: {
            get() {
                return _.get(this.form, 'description', '')
            },
            set(value) {
                this.eventStore.setFormDescription(value);
            }
        },
        booking: {
            get() {
                return _.get(this.form, 'booking', 'none') === 'free'
            },
            set(value) {
                if (value) this.eventStore.setFormBooking('free');
                else this.eventStore.setFormBooking('none');
            }
        },
        useQrCode: {
            get() {
                return !!_.get(this.form, 'use_qr_code', false)
            },
            set(value) {
                this.eventStore.setFormUseQrCode(value);
            }
        },
        member_limit_per_slot: {
            get() {
                return _.get(this.form, 'member_limit_per_slot', '')
            },
            set(value) {
                this.eventStore.setFormMemberLimitPerSlot(value);
            }
        },
        registrationTemplateId: {
            get() {
                return _.get(this.form, 'registrationTemplate.id') || null
            },
            set(value) {
                const template = value ? _.find(this.registrationTemplateOptions, { id: value }) : null
                this.eventStore.setFormRegistrationTemplate(template || null);
            }
        },
        // Configurable registration forms exist for the citystore-ssr storefront only
        hasRegistrationTemplates() {
            return this.setupStore.enabledFeatures.includes('event-manager:registration-templates')
        },
        registrationTemplateOptions() {
            return _.get(this.options, 'registrationTemplate.items') || []
        },
        registrationTemplateOptionsLoading() {
            return !!_.get(this.options, 'registrationTemplate.loading')
        },
        bookingNotificationText: {
            get() {
                return _.get(this.form, 'booking_notification_text', '')
            },
            set(value) {
                this.eventStore.setFormBookingNotificationText(value);
            }
        },
        countryOptions() {
            return countries.getNames('de')
        },
        street: {
            get() {
                return _.get(this.form, 'address.street', '')
            },
            set(value) {
                this.eventStore.setFormStreet(value);
            }
        },
        zipcode: {
            get() {
                return _.get(this.form, 'address.zipcode', '')
            },
            set(value) {
                this.eventStore.setFormZipcode(value);
            }
        },
        city: {
            get() {
                return _.get(this.form, 'address.city', '')
            },
            set(value) {
                this.eventStore.setFormCity(value);
            }
        },
        country: {
            get() {
                return _.get(this.form, 'address.country', '')
            },
            set(value) {
                this.eventStore.setFormCountry(value);
            }
        },
        category: {
            get() {
                return _.get(this.form, 'category')
            },
            set(value) {
                this.eventStore.setFormCategory(value);
            }
        },
        categoryOptions() {
            let options = this.options.category.items
            return options
        },
        // isUpdate() {
        //     return !! _.get(this.form, 'id')
        // },
        activeCms() {
            return useSetupStore().activeCms;
        },
        form() {
            return this.eventStore.form;
        },
        options() {
            return this.eventStore.options;
        },
    },
    methods: {
        onImageUpload(response) {
            if (response.id) this.eventStore.setFormBanner({
                id: response.id,
                url: response.url,
                name: response.name,
                mime: response.mime,
                width: _.get(response, 'dimensions.width'),
                height: _.get(response, 'dimensions.height'),
                // Frischer Upload → liegt (bis zum Speichern) in der uploads-Tabelle,
                // damit die Crop-Vorschau /storage/public/uploads/{id}/{name} nutzt.
                upload: true,
            })
        },
        // Aus der Mediathek gewähltes Bild als Event-Banner setzen.
        // Der Banner wird backend-seitig aus einem frischen Upload erzeugt
        // (SaveEvent: Upload::find(banner_id) → Image + File, Upload wird gelöscht).
        // Eine Mediathek-Datei ist aber ein bestehender File, kein Upload – daher
        // laden wir ihre Bytes neu zum Events-Endpoint hoch und übernehmen die
        // resultierende Upload-ID. Gespeichert wird nur die ID, nie eine URL.
        async onLibrarySelected(selection) {
            const image = Array.isArray(selection) ? selection[0] : selection
            if (!image) return
            const url = _.get(image, 'url') || _.get(image, 'source.url')
            if (!url) return

            this.bannerUploading = true
            try {
                const { data: blob } = await axios.get(url, { responseType: 'blob' })
                const name = _.get(image, 'name') || _.get(image, 'source.name') || 'banner'
                const response = await this.uploadBannerBlob(blob, name)
                if (response && response.id) {
                    this.eventStore.setFormBanner({
                        id: response.id,
                        url: response.url,
                        name: response.name,
                        mime: response.mime,
                        width: _.get(response, 'dimensions.width'),
                        height: _.get(response, 'dimensions.height'),
                        upload: true,
                    })
                }
            } catch (error) {
                this.$notify.error({
                    title: this.$t('Upload failed'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                })
            } finally {
                this.bannerUploading = false
            }
        },
        // Lädt einen Blob zum Events-Upload-Endpoint und liefert die Upload-Antwort
        // ({ id, url, name, mime, ... }) zurück – id ist die Upload-ID für banner_id.
        async uploadBannerBlob(blob, fileName) {
            const formData = new FormData()
            formData.append('file', blob, fileName)
            Object.keys(this.uploadData || {}).forEach(key => {
                formData.append(key, this.uploadData[key])
            })

            const token = await ensureFreshToken()
            const { data } = await axios.post(this.uploadEndpoint, formData, {
                headers: { authorization: `Bearer ${token}`, accept: 'application/json' },
            })
            return data
        },
        openImageEditor() {
            this.imageEditorVisible = true
        },
        // Nicht-destruktiver Zuschnitt: nur settings.processing setzen; das Original-File
        // (banner.url) bleibt unverändert. Wird beim Speichern als banner_settings gesendet.
        onBannerCrop(processing) {
            this.eventStore.setFormBannerProcessing(processing)
        },
        // Zuschnitt entfernen → Standard-16:9 (zentriert) bzw. Original.
        onBannerReset() {
            this.eventStore.setFormBannerProcessing(null)
        },
        removeImage() {
            this.eventStore.setFormBanner(null)
        },
        getPageOptions() {
            let menus = _.get(this.activeCms, 'config.template.menus', [])
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))

            const menuOptions = _.get(this.options, 'menu.items') || []
            // const visibleOptgroups = menuOptions.filter(el => el.menuItems.length).length > 1

            let tree = []
            menus.forEach(menu => {
                let dir = { folder: menu.name, children: [] }
                let children = []

                const menuOption = _.find(menuOptions, { key: menu.key })
                let items = _.get(menuOption, 'menuItems', [])

                if (items.length) {
                    items = _.cloneDeep(items)
                    children = NestedSet.createTree(items)
                }

                dir.children = children
                if (children.length) tree.push(dir)
            })

            return tree
        },
    }
}
</script>

<style lang="sass" scoped>
    .el-form-item.colorpicker
        // margin: 0
        ::v-deep
            .el-form-item__content
                height: 36px
    .flex-row
        display: flex
        justify-content: space-between
        .grow
            flex-grow: 1
            margin-right: 1rem
    .registration-template
        .help
            line-height: 1.4
            margin-top: 6px
        .option-hint
            float: right
            margin-left: 12px
            font-size: 12px
            color: $color-grey-text
    .category-option
        .color
            width: 12px
            height: 12px
            border-radius: 50%
            display: inline-block
            margin-right: 6px
            border: 1px solid $--border-color-base
            position: relative
            bottom: -1px
            margin-left: -8px
    .slide-fade-enter-active
        transition: all 0.3s ease-out
    .slide-fade-leave-active
        transition: all 0.2s ease-in
    .slide-fade-enter,
    .slide-fade-leave-to
        transform: translateY(-10px)
        opacity: 0

    .event-image-upload
        .event-image-preview
            position: relative
            display: inline-block
            width: 100%
            .image-link
                display: block
                position: relative
                cursor: pointer
                border-radius: 6px
                overflow: hidden
                &:hover .edit-popover
                    opacity: 1
            .banner-frame
                position: relative
                width: 100%
                padding-top: 56.25%
                border-radius: 6px
                border: 1px solid #e4e7ed
                overflow: hidden
                background: #f5f7fa
                img
                    position: absolute
                    top: 0
                    left: 0
                    width: 100%
                    height: 100%
                    object-fit: cover
            .edit-popover
                position: absolute
                top: 0
                left: 0
                right: 0
                bottom: 0
                background: rgba(0, 0, 0, 0.45)
                display: flex
                align-items: center
                justify-content: center
                opacity: 0
                transition: opacity 0.15s ease
                i
                    color: #fff
                    font-size: 24px
            .remove-image
                position: absolute
                top: 6px
                right: 6px
                opacity: 0.85
                z-index: 2
        .select-library-btn
            margin-top: 10px
        .event-upload-box
            width: 100%
            ::v-deep
                .el-upload
                    width: 100%
                .el-upload-dragger
                    width: 100%
                    height: 120px
                    display: flex
                    flex-direction: column
                    align-items: center
                    justify-content: center
                    gap: 8px
                    i
                        font-size: 28px
                        color: #c0c4cc
                    .el-upload__text
                        font-size: 13px
                        color: #909399

    .qr-option
        ::v-deep .el-form-item__content
            line-height: 1

        .qr-option-inner
            display: flex
            align-items: center
            justify-content: space-between
            gap: 16px
            background: #f4f8ff
            border: 1px solid #d0e3ff
            border-radius: 6px
            padding: 12px 16px

        .qr-option-text
            flex: 1
            min-width: 0

        .qr-option-label
            font-size: 14px
            font-weight: 600
            color: #303133
            i
                color: $--color-primary
                margin-right: 4px

        .qr-option-desc
            font-size: 12px
            color: #909399
            margin: 4px 0 0
            line-height: 1.5
</style>
